import { Link } from 'react-router-dom';
import { Seo, Mark, Container } from '../components/marketing/Primitives';

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About"
        description="Every medical reference platform makes an editorial decision about what you should know. EdAI's content is derived differently: from the sources the profession's own authorities designate."
      />

      <section className="border-b border-hairline bg-surface">
        <Container className="py-20 md:py-24">
          <Mark>The core story</Mark>
          <h1 className="mt-4 max-w-[20ch] font-serif text-4xl leading-tight md:text-5xl">
            Whose knowledge is it, anyway?
          </h1>
        </Container>
      </section>

      <section className="py-20 md:py-24">
        <Container>
          <div className="space-y-16">
            <article className="grid gap-6 md:grid-cols-[1fr_2fr] md:gap-16">
              <h2 className="font-serif text-2xl">The contrast</h2>
              <p className="max-w-measure-wide leading-relaxed text-slate">
                Every medical reference platform makes an editorial decision about what you should know. An editor, an
                algorithm, a crawl of the literature: someone at the company decides what makes the cut. That is
                editorially-derived content.
              </p>
            </article>
            <article className="grid gap-6 border-t border-hairline pt-16 md:grid-cols-[1fr_2fr] md:gap-16">
              <h2 className="font-serif text-2xl">The inversion</h2>
              <p className="max-w-measure-wide leading-relaxed text-slate">
                EdAI’s content is derived differently. Specialty boards designate the sources their diplomates are
                accountable for. Licensing blueprints specify, jurisdiction by jurisdiction, the knowledge a physician
                must demonstrate. EdAI takes its content directly from those designated sources, scoped by those
                blueprints, and nothing else. The blueprint is not a test outline; it is a curation instrument.
              </p>
            </article>
            <article className="grid gap-6 border-t border-hairline pt-16 md:grid-cols-[1fr_2fr] md:gap-16">
              <h2 className="font-serif text-2xl">The modalities</h2>
              <p className="max-w-measure-wide leading-relaxed text-slate">
                The result is one validated, provenance-tracked corpus rendered into every modality of education and
                assessment, across the entire arc of a medical career: student, licensure candidate, diplomate,
                recertifying practitioner.
              </p>
            </article>
          </div>
        </Container>
      </section>

      <section className="bg-ink py-20 text-paper-dark-text md:py-24">
        <Container>
          <Mark dark>The founder</Mark>
          <h2 className="mt-3 max-w-[22ch] font-serif text-3xl md:text-4xl">Built by the examiner and the examined.</h2>
          <div className="mt-8 grid gap-10 lg:grid-cols-[2fr_1fr] lg:gap-16">
            <div>
              <p className="max-w-measure-wide leading-relaxed text-slate-dark-text">
                John C. Ferguson, MD, FACS holds five board certifications and has spent more than ten years creating
                and administering specialty board examinations, including as Chair of the ABCS Written Exam Committee
                and President-Elect of the ABFCS. He is a published author on Content-Driven Intelligence and a
                recognized thought leader in AI in medicine, healthcare, education, and ethics (AI 2030 Global Fellow).
                He has lived both sides of the problem EdAI solves: the diplomate accountable to the standard, and the
                examiner responsible for defining and defending it.
              </p>
              <blockquote className="mt-8 border-l-2 border-seal pl-5 font-serif text-xl italic leading-relaxed">
                The boards already know what physicians must know. What was missing was the infrastructure to use that
                knowledge at scale, without ever compromising what makes it valid.
              </blockquote>
            </div>
            <div className="border-hairline-dark lg:border-l lg:pl-10">
              <h3 className="font-serif text-lg">Why medicine</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-dark-text">
                Because it is the one domain where the stakes are human life, and therefore the one domain where
                editorially-derived, hallucination-prone AI is unacceptable. The domain chose the architecture.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[26ch] font-serif text-3xl md:text-4xl">
            Authority-derived content. Every modality of learning.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">
              Request a demo
            </Link>
            <Link to="/who-its-for" className="btn-secondary">
              Find your platform
            </Link>
          </div>
        </Container>
      </section>
    </>
  );
}
